<template>
  <Toast />
  <div class="login-page" :class="{ 'dark-mode': isDarkTheme }">
    <div class="login-container">
      <!-- Left Panel - Branding -->
      <div class="login-branding">
        <div class="branding-overlay"></div>
        <div class="branding-content">
          <img :src="logoUrl" :alt="companyName" class="brand-logo" />
          <h1 class="brand-title">{{ companyName }}</h1>
          <p class="brand-subtitle">Postal Shipment Management System</p>
          <div class="brand-features">
            <div class="feature-item">
              <i class="pi pi-shield"></i>
              <span>High Security</span>
            </div>
            <div class="feature-item">
              <i class="pi pi-bolt"></i>
              <span>Fast Performance</span>
            </div>
            <div class="feature-item">
              <i class="pi pi-chart-bar"></i>
              <span>Advanced Reports</span>
            </div>
          </div>
        </div>
        <div class="branding-decoration">
          <div class="decoration-circle circle-1"></div>
          <div class="decoration-circle circle-2"></div>
          <div class="decoration-circle circle-3"></div>
        </div>
      </div>

      <!-- Right Panel - Form -->
      <div class="login-form-panel">
        <div class="form-wrapper">
          <div class="form-header">
            <img :src="logoUrl" :alt="companyName" class="mobile-logo" />
            <h2 class="form-title">Welcome Back</h2>
            <p class="form-subtitle">Sign in to continue to your account</p>
          </div>

          <form @submit.prevent="handleLogin" class="login-form">
            <div class="input-group" :class="{ 'input-focused': emailFocused, 'input-filled': email }">
              <div class="input-icon">
                <i class="pi pi-envelope"></i>
              </div>
              <input
                id="email"
                type="email"
                v-model="email"
                @focus="emailFocused = true"
                @blur="emailFocused = false"
                required
                autocomplete="email"
              />
              <label for="email" class="floating-label">Email Address</label>
              <div class="input-line"></div>
            </div>

            <div class="input-group" :class="{ 'input-focused': passwordFocused, 'input-filled': password }">
              <div class="input-icon">
                <i class="pi pi-lock"></i>
              </div>
              <input
                id="password"
                :type="showPassword ? 'text' : 'password'"
                v-model="password"
                @focus="passwordFocused = true"
                @blur="passwordFocused = false"
                required
                autocomplete="current-password"
              />
              <label for="password" class="floating-label">Password</label>
              <button type="button" class="toggle-password" @click="showPassword = !showPassword" tabindex="-1">
                <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
              </button>
              <div class="input-line"></div>
            </div>

            <div class="form-options">
              <label class="remember-me">
                <input type="checkbox" v-model="rememberMe" />
                <span class="checkmark"></span>
                <span>Remember me</span>
              </label>
              <a href="#" class="forgot-password" @click.prevent="handleForgotPassword"> Forgot Password? </a>
            </div>

            <button
              type="submit"
              class="login-button"
              :class="{ 'button-loading': loading }"
              :disabled="loading || !isFormValid"
            >
              <span v-if="!loading" class="button-text">
                <span>Sign In</span>
                <i class="pi pi-arrow-left"></i>
              </span>
              <span v-else class="button-loader">
                <span class="loader-dot"></span>
                <span class="loader-dot"></span>
                <span class="loader-dot"></span>
              </span>
            </button>
          </form>

          <div class="form-footer">
            <p>By signing in, you agree to our <a href="#">Terms of Service</a> and <a href="#">Privacy Policy</a></p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useLayout } from '@/layout/composables/layout';
import { useCompanyBrand } from '@/composables/useCompanyBrand';
import { saveCredentials, saveToken } from '@/utils/auth';
import GlobalService from '../service/GlobalService';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import Toast from 'primevue/toast';

const GlobalServiceItem = ref(new GlobalService());
const { layoutConfig, isDarkTheme } = useLayout();
const { companyName, companyLogoUrl } = useCompanyBrand();
const router = useRouter();
const toast = useToast();

const email = ref('');
const password = ref('');
const loading = ref(false);
const showPassword = ref(false);
const rememberMe = ref(false);
const emailFocused = ref(false);
const passwordFocused = ref(false);

const logoUrl = computed(() => {
  if (companyLogoUrl.value) return companyLogoUrl.value;
  return `/layout/images/${layoutConfig.darkTheme.value ? 'logo-white' : 'logo-dark'}.svg`;
});

const isFormValid = computed(() => {
  return email.value.length > 0 && password.value.length > 0;
});

const handleLogin = async () => {
  if (!isFormValid.value || loading.value) return;

  loading.value = true;
  const loginData = {
    email: email.value,
    password: password.value
  };

  try {
    const data = await GlobalServiceItem.value.login(loginData);
    saveToken(data.data.data.token);
    saveCredentials(data.data.data.user?.data ?? data.data.data.user);
    toast.add({
      severity: 'success',
      summary: 'Login Successful',
      detail: 'Welcome back! You have been logged in.',
      life: 3000
    });
    router.push('/');
  } catch (error) {
    const message = error?.response?.data?.message || 'Invalid email or password. Please try again.';
    toast.add({
      severity: 'error',
      summary: 'Login Failed',
      detail: message,
      life: 4000
    });
  } finally {
    loading.value = false;
  }
};

const handleForgotPassword = () => {
  toast.add({
    severity: 'info',
    summary: 'Password Recovery',
    detail: 'Please contact support to reset your password.',
    life: 4000
  });
};
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-ground);
  font-family: var(--font-family);
}

.login-container {
  display: flex;
  width: 100%;
  max-width: 1000px;
  min-height: 600px;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
  background: var(--surface-card);
  margin: 1rem;
}

/* Left Panel - Branding */
.login-branding {
  flex: 1;
  position: relative;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-700) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  overflow: hidden;
}

.branding-overlay {
  position: absolute;
  inset: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.5;
}

.branding-content {
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
}

.brand-logo {
  width: 80px;
  height: 80px;
  margin-bottom: 1.5rem;
  filter: brightness(0) invert(1);
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.brand-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.5px;
}

.brand-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
  margin: 0 0 2.5rem 0;
  font-weight: 300;
}

.brand-features {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;
  opacity: 0.9;
  justify-content: center;
}

.feature-item i {
  font-size: 1.1rem;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 4px;
}

/* Decoration Circles */
.branding-decoration {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.decoration-circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
}

.circle-1 {
  width: 300px;
  height: 300px;
  top: -100px;
  right: -100px;
  animation: pulse 4s ease-in-out infinite;
}

.circle-2 {
  width: 200px;
  height: 200px;
  bottom: -50px;
  left: -50px;
  animation: pulse 5s ease-in-out infinite 0.5s;
}

.circle-3 {
  width: 150px;
  height: 150px;
  bottom: 30%;
  right: 10%;
  animation: pulse 6s ease-in-out infinite 1s;
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.08;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.12;
  }
}

/* Right Panel - Form */
.login-form-panel {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  background: var(--surface-card);
}

.form-wrapper {
  width: 100%;
  max-width: 380px;
}

.mobile-logo {
  display: none;
  width: 48px;
  height: 48px;
  margin-bottom: 1.5rem;
}

.form-header {
  margin-bottom: 2.5rem;
}

.form-title {
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--text-color);
  margin: 0 0 0.5rem 0;
}

.form-subtitle {
  color: var(--text-color-secondary);
  margin: 0;
  font-size: 1rem;
}

/* Input Groups */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.input-group {
  position: relative;
  padding-top: 1.25rem;
}

.input-group input {
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 3rem;
  font-size: 1rem;
  border: 2px solid var(--surface-border);
  border-radius: 12px;
  background: var(--surface-section);
  color: var(--text-color);
  transition: all 0.3s ease;
  outline: none;
  font-family: var(--font-family);
}

.input-group input:focus {
  border-color: var(--primary-color);
  background: var(--surface-card);
  box-shadow: 0 0 0 4px rgba(30, 64, 175, 0.1);
}

.dark-mode .input-group input:focus {
  box-shadow: 0 0 0 4px rgba(96, 165, 250, 0.15);
}

.input-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color-secondary);
  font-size: 1.1rem;
  z-index: 2;
  transition: color 0.3s ease;
  margin-top: 0.625rem;
}

.input-group.input-focused .input-icon {
  color: var(--primary-color);
}

.floating-label {
  position: absolute;
  left: 3rem;
  top: 1.75rem;
  color: var(--text-color-secondary);
  font-size: 0.95rem;
  pointer-events: none;
  transition: all 0.3s ease;
  background: var(--surface-card);
  padding: 0 0.25rem;
}

.input-group.input-focused .floating-label,
.input-group.input-filled .floating-label {
  top: 0.25rem;
  left: 1rem;
  font-size: 0.75rem;
  color: var(--primary-color);
  font-weight: 600;
}

.input-line {
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 2px;
  background: var(--primary-color);
  transition: all 0.3s ease;
  border-radius: 0 0 12px 12px;
}

.input-group.input-focused .input-line {
  left: 0;
  width: 100%;
}

.toggle-password {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-color-secondary);
  cursor: pointer;
  font-size: 1.1rem;
  padding: 0.25rem;
  z-index: 2;
  margin-top: 0.625rem;
  transition: color 0.3s ease;
}

.toggle-password:hover {
  color: var(--primary-color);
}

/* Form Options */
.form-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.5rem;
}

.remember-me {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.9rem;
  color: var(--text-color-secondary);
  user-select: none;
}

.remember-me input[type='checkbox'] {
  display: none;
}

.checkmark {
  width: 18px;
  height: 18px;
  border: 2px solid var(--surface-300);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.remember-me input[type='checkbox']:checked + .checkmark {
  background: var(--primary-color);
  border-color: var(--primary-color);
}

.remember-me input[type='checkbox']:checked + .checkmark::after {
  content: '';
  width: 5px;
  height: 9px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  margin-top: -2px;
}

.forgot-password {
  font-size: 0.9rem;
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;
  transition: opacity 0.2s ease;
}

.forgot-password:hover {
  opacity: 0.8;
}

/* Login Button */
.login-button {
  width: 100%;
  padding: 1rem;
  font-size: 1.05rem;
  font-weight: 600;
  color: white;
  background: var(--primary-color);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  font-family: var(--font-family);
  margin-top: 0.5rem;
}

.login-button:hover:not(:disabled) {
  background: var(--primary-700);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(30, 64, 175, 0.3);
}

.dark-mode .login-button:hover:not(:disabled) {
  box-shadow: 0 8px 20px rgba(96, 165, 250, 0.3);
}

.login-button:active:not(:disabled) {
  transform: translateY(0);
}

.login-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.button-text {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.button-text i {
  transition: transform 0.3s ease;
}

.login-button:hover:not(:disabled) .button-text i {
  transform: translateX(-4px);
}

.button-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.loader-dot {
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out;
}

.loader-dot:nth-child(1) {
  animation-delay: -0.32s;
}
.loader-dot:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

/* Form Footer */
.form-footer {
  margin-top: 2rem;
  text-align: center;
}

.form-footer p {
  font-size: 0.85rem;
  color: var(--text-color-secondary);
  margin: 0;
  line-height: 1.6;
}

.form-footer a {
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;
}

.form-footer a:hover {
  text-decoration: underline;
}

/* Responsive Design */
@media (max-width: 768px) {
  .login-container {
    flex-direction: column;
    max-width: 420px;
    min-height: auto;
    margin: 0.5rem;
    border-radius: 20px;
  }

  .login-branding {
    display: none;
  }

  .mobile-logo {
    display: block;
  }

  .login-form-panel {
    padding: 2rem 1.5rem;
  }

  .form-title {
    font-size: 1.5rem;
  }
}

@media (max-width: 480px) {
  .login-form-panel {
    padding: 1.5rem 1rem;
  }

  .form-options {
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-start;
  }
}
</style>
